import { useEffect, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AsyncSection, Button, Modal } from "@playbookai/ui";
import {
  adminMemberSessionsQueryKey,
  listMemberSessions,
  revokeMemberSessions,
} from "../../api/admin-users-api.js";
import { ME_QUERY_KEY } from "../../auth/auth-store.js";
import { deviceLabel } from "./device-label.js";

/**
 * A member's live sign-in sessions, and the button that ends them (PB-2927).
 *
 * Lives on the Members & roles row rather than in its own admin section: the
 * administrator is already looking at the person, and a section would need a
 * user picker to answer a question the row already answers.
 *
 * The list and confirmation are two stages of this one modal. Product requires
 * an explicit final check because the action immediately invalidates every
 * device, including the administrator's own session when they target themself.
 */

export interface SessionsModalProps {
  /** The member whose sessions to show; `null` keeps the dialog closed. */
  readonly member: { readonly userId: string; readonly label: string } | null;
  /** The acting tenant, for the cache key. Passed in rather than read from the
      store here: `MembersAdmin` already fences the open dialog on this exact
      value, and two readers of the same fact can disagree. */
  readonly tenantId: string | null;
  /** Whether the member IS the acting administrator. Ending your own sessions is
      allowed — it is reversible by signing back in — but it must also tell the
      shell its session is gone. */
  readonly isSelf?: boolean;
  readonly onClose: () => void;
}

/** What a revoke submission carries, captured at click time. */
interface RevokeTarget {
  readonly userId: string;
  readonly tenantId: string | null;
  readonly isSelf: boolean;
}

function relativeTime(unixSeconds: number): string {
  if (!unixSeconds) return "unknown";
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - unixSeconds);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function SessionsModal({
  member,
  tenantId,
  isSelf = false,
  onClose,
}: SessionsModalProps): ReactElement {
  const queryClient = useQueryClient();
  const userId = member?.userId ?? null;
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setConfirming(false), [userId]);
  /* From the api module so the tenant segment is structural and this query
     cannot forget it, per the PB-1556 convention. */
  const queryKey = adminMemberSessionsQueryKey(tenantId, userId);

  const sessions = useQuery({
    queryKey,
    queryFn: ({ signal }) => listMemberSessions(userId!, signal),
    enabled: userId !== null,
  });

  /* The target is captured in mutation VARIABLES, not read from the closure in
     `onSuccess`. The dialog can be closed while the request is in flight — a
     ~200ms window the admin can hit by clicking Close straight after End — and
     by the time the callback runs, `member` is null, so a closure read would
     invalidate the key for `userId: null` and, worse, compute `isSelf` as false
     and never tell the shell the admin just signed themselves out. Same lesson
     as the tenant fence on the dialog state above: a render-time value is not
     safe to reuse in an async callback. */
  const revoke = useMutation({
    mutationFn: (target: RevokeTarget) => revokeMemberSessions(target.userId),
    onSuccess: (_result, target) => {
      /* Refetch rather than assume: the empty list the administrator then sees
         is the server's answer, not this component's optimism.

         NOT awaited. Returning the promise keeps the mutation `isPending` until
         the refetch settles, and when an administrator ends their OWN sessions
         that refetch is already unauthenticated — it 401s, retries, and the
         button sits on "Signing out..." indefinitely. Observed in the browser;
         the revocation itself had already succeeded. */
      void queryClient.invalidateQueries({
        queryKey: adminMemberSessionsQueryKey(target.tenantId, target.userId),
      });

      /* Ending your own sessions means you are signed out NOW. The shell learns
         that from `/api/auth/me`, which is held fresh for five minutes, so
         without this the admin keeps a working-looking UI on a dead session
         until that query happens to refetch. Invalidating it lets the normal
         unauthenticated gate take over immediately. */
      if (target.isSelf) {
        /* Close FIRST. Invalidating /me tears down the authenticated shell; the
           invalidation remains deliberately non-awaited so an unauthenticated
           refetch cannot strand the mutation on "Signing out…". */
        onClose();
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      } else {
        setConfirming(false);
      }
    },
  });

  const rows = sessions.data ?? [];
  const busy = revoke.isPending || sessions.isFetching;

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      title={`Sign-in sessions — ${member?.label ?? ""}`}
      description="Ending sessions signs the member out on every device. They keep their access and can sign in again."
      size="sm"
      footer={
        confirming ? (
          <>
            <Button
              variant="secondary"
              disabled={revoke.isPending}
              onClick={() => setConfirming(false)}
            >
              Back
            </Button>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() =>
                userId && revoke.mutate({ userId, tenantId, isSelf })
              }
            >
              {revoke.isPending ? "Signing out..." : "Confirm and sign out"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="destructive"
              disabled={busy || rows.length === 0}
              onClick={() => setConfirming(true)}
            >
              End all sessions
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {confirming ? (
          <div className="rounded-md border border-pb-border bg-pb-card-elevated p-3">
            <p className="font-semibold text-pb-text">
              End {rows.length} active{" "}
              {rows.length === 1 ? "session" : "sessions"}?
            </p>
            <p className="mt-1 text-sm text-pb-text-muted">
              {isSelf
                ? "This includes your current session. You will be signed out and will need to sign in again."
                : "The member will be signed out on every listed device and can sign in again."}
            </p>
          </div>
        ) : sessions.isError ? (
          <AsyncSection
            status="error"
            errorTitle="Couldn't load the sessions"
            errorDescription={
              sessions.error instanceof Error ? sessions.error.message : null
            }
            onRetry={() => {
              void sessions.refetch();
            }}
          >
            {null}
          </AsyncSection>
        ) : sessions.isLoading ? (
          <p role="status" className="text-sm text-pb-text-muted">
            Loading sessions...
          </p>
        ) : rows.length === 0 ? (
          /* Stated explicitly rather than shown as an empty table, so "signed
             out everywhere" and "failed to load" can never look alike. */
          <p
            role="status"
            data-testid="no-sessions"
            className="text-sm text-pb-text-muted"
          >
            No active sessions.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((session, index) => (
              <li
                // No stable id by design — the session id is a credential and is
                // never sent. The list is refetched whole, so positional keys
                // cannot strand state across a reorder.
                key={`${session.createdAt}-${index}`}
                className="flex items-baseline justify-between gap-3 rounded-md border border-pb-border bg-pb-card-elevated px-3 py-2"
              >
                <span className="text-sm text-pb-text">
                  {deviceLabel(session.userAgent)}
                </span>
                <span className="text-dense-1 text-pb-text-muted">
                  active {relativeTime(session.updatedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {revoke.isError ? (
          <p role="alert" className="text-sm text-pb-red-text">
            {revoke.error instanceof Error
              ? revoke.error.message
              : "Could not end the sessions."}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
